/**
 * The contents of a site as a tree by public address, in one language.
 *
 * A migrated site holds hundreds of contents, Italian and English side by
 * side. Read as one flat list they hide the shape of the site, so this module
 * does three things, all pure:
 *  1. pairs translations (same `translationGroup`) into one entry shown in the
 *     language being worked on, with the others as compact labels;
 *  2. says which languages an entry is missing;
 *  3. draws the entries as a tree by public path: single pages first (home
 *     before all), then sections («Blog», «Corsi»…), each with its count.
 *
 * Entries that exist only in another language cannot hang from the tree of
 * the current one: they go in one section of their own, grouped by type, so
 * an unlinked translation is visible instead of silently missing.
 */

import type { ContentSummary } from '@cms/contracts'
import { contentPath } from './live-url.js'

export interface TypeInfo {
  label: string
  urlPattern?: string | null
}

export interface TreeEntry {
  /** The translation group, or `solo:<id>` for a content that has none. */
  key: string
  /** The member shown: the one in the current language, else the first. */
  main: ContentSummary
  /** True when `main` is in the language being viewed. */
  inLocale: boolean
  /** The other members, in the order of `locales`. */
  translations: ContentSummary[]
  /** Languages the entry has no member in (the current one included when `inLocale` is false). */
  missing: string[]
  /** The public path of `main`, when it can be known. */
  path: string | null
}

export interface TreeNode {
  /** Stable across renders: used for the open/closed state. */
  key: string
  label: string
  /** Set on leaves; a section that is also a page has its page as first child. */
  entry: TreeEntry | null
  children: TreeNode[]
  /** Entries below (a leaf counts 1). */
  count: number
  /** A section made up by the screen (a type, «other languages»), not by a real path. */
  virtual: boolean
}

export interface TreeContext {
  locale: string
  /** Every language of the site, main one first. */
  locales: readonly string[]
  types: ReadonlyMap<string, TypeInfo>
}

/** The shown language first, then the others as the site lists them, then any found only in data. */
export function orderedLocales(siteLocales: readonly string[], defaultLocale: string | null | undefined, items: readonly ContentSummary[]): string[] {
  const found = new Set<string>()
  if (defaultLocale) found.add(defaultLocale)
  for (const locale of siteLocales) found.add(locale)
  for (const item of items) found.add(item.locale)
  return [...found]
}

export function titleOf(item: ContentSummary): string {
  const title = item.title?.trim()
  return title ? title : item.slug
}

function newest(a: ContentSummary, b: ContentSummary): ContentSummary {
  return b.updatedAt > a.updatedAt ? b : a
}

export function buildEntries(items: readonly ContentSummary[], context: TreeContext): TreeEntry[] {
  const groups = new Map<string, ContentSummary[]>()
  for (const item of items) {
    const key = item.translationGroup ? item.translationGroup : `solo:${item.id}`
    const list = groups.get(key)
    if (list) list.push(item)
    else groups.set(key, [item])
  }
  const rank = (locale: string) => {
    const index = context.locales.indexOf(locale)
    return index < 0 ? context.locales.length : index
  }
  const entries: TreeEntry[] = []
  for (const [key, members] of groups) {
    const inView = members.filter((member) => member.locale === context.locale)
    const inLocale = inView.length > 0
    const main = inLocale ? inView.reduce(newest) : [...members].sort((a, b) => rank(a.locale) - rank(b.locale))[0]!
    const translations = members
      .filter((member) => member.id !== main.id && !(inLocale && member.locale === context.locale))
      .sort((a, b) => rank(a.locale) - rank(b.locale))
    const present = new Set(members.map((member) => member.locale))
    const missing = context.locales.filter((locale) => !present.has(locale))
    entries.push({ key, main, inLocale, translations, missing, path: publicPath(main, context) })
  }
  return entries
}

/** The address as the site serves it: its own `path`, else the type's pattern; null when neither says. */
export function publicPath(item: ContentSummary, context: Pick<TreeContext, 'types'>): string | null {
  const own = item.path
  if (typeof own === 'string' && own.startsWith('/')) return own
  return contentPath(context.types.get(item.contentTypeKey)?.urlPattern, null, item.slug)
}

function fold(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

export function filterEntries(
  entries: readonly TreeEntry[],
  filter: { type?: string; status?: string; words?: string },
  typeLabel: (key: string) => string,
): TreeEntry[] {
  const words = fold(filter.words ?? '').split(/\s+/).filter(Boolean)
  return entries.filter((entry) => {
    if (filter.type && entry.main.contentTypeKey !== filter.type) return false
    if (filter.status && entry.main.status !== filter.status) return false
    if (words.length === 0) return true
    const haystack = fold([
      entry.path ?? '',
      typeLabel(entry.main.contentTypeKey),
      ...[entry.main, ...entry.translations].flatMap((member) => [member.title ?? '', member.slug]),
    ].join(' '))
    return words.every((word) => haystack.includes(word))
  })
}

/** `case-studies` → `Case studies`. */
export function humanizeSegment(segment: string): string {
  let text = segment
  try {
    text = decodeURIComponent(segment)
  } catch {
    // Left as written.
  }
  text = text.replace(/[-_]+/g, ' ').trim()
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : segment
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
}

/** Path segments of an entry, a leading language prefix (`/en/…`) removed. */
function segmentsOf(entry: TreeEntry, context: TreeContext): string[] | null {
  if (entry.path === null) return null
  const segments = entry.path.split('/').filter(Boolean)
  if (segments.length > 1 && segments[0] === entry.main.locale && entry.main.locale !== context.locales[0]) segments.shift()
  return segments
}

function node(key: string, label: string, virtual: boolean): TreeNode {
  return { key, label, entry: null, children: [], count: 0, virtual }
}

/**
 * Builds the tree. `otherLabel` names the section for entries that do not
 * exist in the shown language; `typeLabel` names the sections made by type
 * for entries whose path cannot be known.
 */
export function buildTree(
  entries: readonly TreeEntry[],
  context: TreeContext,
  names: { typeLabel: (key: string) => string; otherLabel: string },
): TreeNode[] {
  const root = node('', '', true)
  const other = node('~other', names.otherLabel, true)

  const insert = (parent: TreeNode, prefix: string, segments: readonly { id: string; label: string; virtual: boolean }[], entry: TreeEntry) => {
    let current = parent
    let key = prefix
    segments.forEach((segment, index) => {
      key = `${key}/${segment.id}`
      const last = index === segments.length - 1
      if (last) {
        const leaf = node(`${key}@${entry.key}`, titleOf(entry.main), false)
        leaf.entry = entry
        leaf.count = 1
        // Two contents at the same address (e.g. a draft and its published twin) stay both visible.
        current.children.push(leaf)
        return
      }
      let next = current.children.find((child) => child.entry === null && child.key === key)
      if (!next) {
        next = node(key, segment.label, segment.virtual)
        current.children.push(next)
      }
      current = next
    })
    if (segments.length === 0) {
      const leaf = node(`${prefix}/~home@${entry.key}`, titleOf(entry.main), false)
      leaf.entry = entry
      leaf.count = 1
      current.children.push(leaf)
    }
  }

  for (const entry of entries) {
    const typeSegment = { id: `~${entry.main.contentTypeKey}`, label: names.typeLabel(entry.main.contentTypeKey), virtual: true }
    if (!entry.inLocale) {
      insert(other, '~other', [typeSegment, { id: entry.main.id, label: titleOf(entry.main), virtual: false }], entry)
      continue
    }
    const segments = segmentsOf(entry, context)
    if (segments === null) {
      insert(root, '', [typeSegment, { id: entry.main.id, label: titleOf(entry.main), virtual: false }], entry)
      continue
    }
    insert(root, '', segments.map((segment) => ({ id: segment, label: humanizeSegment(segment), virtual: false })), entry)
  }
  if (other.children.length > 0) root.children.push(other)

  const finish = (current: TreeNode): void => {
    for (const child of current.children) finish(child)
    current.count = current.entry ? 1 : current.children.reduce((sum, child) => sum + child.count, 0)
    const isHome = (child: TreeNode) => child.entry?.path === '/'
    current.children.sort((a, b) => {
      const aLeaf = a.entry !== null
      const bLeaf = b.entry !== null
      if (aLeaf !== bLeaf) return aLeaf ? -1 : 1
      if (isHome(a) !== isHome(b)) return isHome(a) ? -1 : 1
      return compareText(a.label, b.label)
    })
  }
  finish(root)
  // «Other languages» is a footnote of the tree, not a section among the others.
  root.children.sort((a, b) => (a === other ? 1 : b === other ? -1 : 0))
  return root.children
}

/** Keys of every section (nodes with children), for «expand all». */
export function sectionKeys(nodes: readonly TreeNode[]): string[] {
  const keys: string[] = []
  const walk = (list: readonly TreeNode[]) => {
    for (const current of list) {
      if (current.children.length > 0) { keys.push(current.key); walk(current.children) }
    }
  }
  walk(nodes)
  return keys
}

/** Entries per type, for the filter chips. */
export function countByType(entries: readonly TreeEntry[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const entry of entries) counts.set(entry.main.contentTypeKey, (counts.get(entry.main.contentTypeKey) ?? 0) + 1)
  return counts
}
